import DateFormats from '@/core/constants/DateFormats';
import { PASSWORD_STATUS } from '@/core/constants/password';
import moment from 'moment';

export default {
  namespaced: true,

  state: {
    showModal: false,
    steps: [],
    currentStep: 1,
  },

  getters: {
    showModal: (state) => state.showModal,
    hasSteps: (state) => state.steps.length > 0,
    currentStepVal: (state) => state.currentStep,
    steps: (state) => {
      return state.steps.map((step, index) => {
        return {
          ...step,
          value: index + 1,
          visited: index === 0 ? true : step.visited || false,
        };
      });
    },
  },

  mutations: {
    SET_SHOW_MODAL(state, showValue) {
      const wasClosed = !state.showModal;
      state.showModal = showValue;

      // RESET CURRENTSTEP TO 1 ON CLOSE->OPEN
      if (showValue && wasClosed) {
        state.currentStep = 1;
      }
    },
    ADD_STEP(state, step) {
      const isStepInList =
        state.steps.findIndex((s) => s.key === step.key) >= 0;

      if (!isStepInList) {
        state.steps.push(step);
      }
    },

    REMOVE_STEP(state, stepKey) {
      const stepIndex = state.steps.findIndex((s) => s.key === stepKey);

      if (stepIndex >= 0) {
        state.steps.splice(stepIndex, 1);
        if (state.steps.length > 0) {
          if (state.currentStep > state.steps.length) {
            state.currentStep = state.steps.length;
          }
        } else {
          state.showModal = false;
        }
      }
    },

    UPDATE_STEP(state, step) {
      const index = state.steps.findIndex((s) => s.key === step.key);
      if (index >= 0) {
        state.steps.splice(index, 1, step);
      }
    },

    REMOVE_STEPS_BY_PREFIX(state, prefix) {
      state.steps = state.steps.filter((step) => !step.key.startsWith(prefix));
    },

    CHANGE_STEP(state, stepValue) {
      state.currentStep = stepValue;
    },

    CLEAN_STEPS(state) {
      state.steps = [];
      state.currentStep = 1;
    },
  },

  actions: {
    async loadBillingModuleStatusStep({ commit, rootGetters }) {
      if (rootGetters['moduleMaintenanceStatus/isBillingModuleActive']) {
        commit('ADD_STEP', {
          key: 'billingModuleStatus',
          text: 'Ventana de Mantenimiento',
          doneRequired: false,
        });
      }
      commit('REMOVE_STEP', 'notifications');
      commit('REMOVE_STEP', 'notificationPreview');
      commit('REMOVE_STEP', 'reminders');
      commit('REMOVE_STEP', 'updatePassword');
      commit('REMOVE_STEP', 'updateData');
      commit('REMOVE_STEP', 'shoppingCartAlerts');
      commit('REMOVE_STEPS_BY_PREFIX', 'scheduleEventConfirmation');
    },
    async loadUpdateDataStep({ commit, rootGetters }) {
      if (rootGetters['updateData/mustUpdateData']) {
        commit('ADD_STEP', {
          key: 'updateData',
          text: 'Actualización de datos de contacto',
          doneRequired: true,
        });
      }
      commit('REMOVE_STEP', 'notifications');
      commit('REMOVE_STEP', 'notificationPreview');
      commit('REMOVE_STEP', 'reminders');
      commit('REMOVE_STEP', 'updatePassword');
      commit('REMOVE_STEP', 'shoppingCartAlerts');
      commit('REMOVE_STEP', 'billingModuleStatus');
      commit('REMOVE_STEP', 'recentOrders');
      commit('REMOVE_STEPS_BY_PREFIX', 'scheduleEventConfirmation');
    },

    async loadRemindersStep({ commit, rootGetters }) {
      if (rootGetters['reminders/hasAvailableReminders']) {
        commit('ADD_STEP', {
          key: 'reminders',
          text: 'Backorders',
          doneRequired: false,
        });
      }
      commit('REMOVE_STEP', 'notifications');
      commit('REMOVE_STEP', 'notificationPreview');
      commit('REMOVE_STEP', 'updateData');
      commit('REMOVE_STEP', 'updatePassword');
      commit('REMOVE_STEP', 'shoppingCartAlerts');
      commit('REMOVE_STEP', 'billingModuleStatus');
      commit('REMOVE_STEP', 'recentOrders');
      commit('REMOVE_STEPS_BY_PREFIX', 'scheduleEventConfirmation');
    },

    async loadRecentOrdersStep({ commit, rootGetters }) {
      if (rootGetters['recentOrders/hasUnprocessedRecentOrders']) {
        commit('ADD_STEP', {
          key: 'recentOrders',
          text: 'Carritos',
          doneRequired: false,
        });
      }
      commit('REMOVE_STEP', 'notifications');
      commit('REMOVE_STEP', 'updateData');
      commit('REMOVE_STEP', 'updatePassword');
      commit('REMOVE_STEP', 'shoppingCartAlerts');
      commit('REMOVE_STEP', 'reminders');
      commit('REMOVE_STEPS_BY_PREFIX', 'scheduleEventConfirmation');
    },

    async loadUpdatePasswordStep({ commit, rootGetters }) {
      if (rootGetters['updatePassword/wantUpdatePassword']) {
        commit('ADD_STEP', {
          key: 'updatePassword',
          text: 'Actualización de contraseña',
          doneRequired: true,
        });
      }
      commit('REMOVE_STEP', 'notifications');
      commit('REMOVE_STEP', 'notificationPreview');
      commit('REMOVE_STEP', 'updateData');
      commit('REMOVE_STEP', 'reminders');
      commit('REMOVE_STEP', 'shoppingCartAlerts');
      commit('REMOVE_STEP', 'billingModuleStatus');
      commit('REMOVE_STEP', 'recentOrders');
      commit('REMOVE_STEPS_BY_PREFIX', 'scheduleEventConfirmation');
    },
    loadShoppingCartAlertsStep(
      { commit, dispatch },
      { doctype, isBackOrder, isConvert }
    ) {
      let text = 'Alertas de Carrito';

      if (isBackOrder) {
        text = 'Pedido Backorder';
      } else {
        switch (doctype) {
          case 'PSIU':
          case 'PSDT':
            text = 'Nuevo Pedido';
            break;
          case 'PSNW':
            text = 'Pedido de Productos Nuevos (PNC)';
            break;
          case 'PSPO':
            text = 'Pedido Bono POP';
            break;
        }

        text = isConvert ? `Convertir a ${text}` : text;
      }

      dispatch('shoppingCartAlerts/setShoppingCartAlertsDoctype', doctype, {
        root: true,
      });
      dispatch(
        'shoppingCartAlerts/setShoppingCartAlertsIsBackOrder',
        isBackOrder,
        { root: true }
      );

      commit('REMOVE_STEP', 'notifications');
      commit('REMOVE_STEP', 'notificationPreview');
      commit('REMOVE_STEP', 'updateData');
      commit('REMOVE_STEP', 'reminders');
      commit('REMOVE_STEP', 'updatePassword');
      commit('REMOVE_STEP', 'shoppingCartAlerts');
      commit('REMOVE_STEP', 'billingModuleStatus');
      commit('REMOVE_STEPS_BY_PREFIX', 'scheduleEventConfirmation');

      commit('ADD_STEP', {
        key: 'shoppingCartAlerts',
        text,
        doneRequired: false,
      });
    },
    async loadNotificationPreviewStep({ commit, rootGetters }) {
      if (rootGetters['userNotification/selectedNotificationDetails']) {
        commit('ADD_STEP', {
          key: 'notificationPreview',
          text: 'Actualización IEL',
          doneRequired: false,
        });
      }
      commit('REMOVE_STEP', 'notifications');
      commit('REMOVE_STEP', 'updateData');
      commit('REMOVE_STEP', 'reminders');
      commit('REMOVE_STEP', 'updatePassword');
      commit('REMOVE_STEP', 'shoppingCartAlerts');
      commit('REMOVE_STEP', 'billingModuleStatus');
      commit('REMOVE_STEP', 'recentOrders');
      commit('REMOVE_STEPS_BY_PREFIX', 'scheduleEventConfirmation');
    },
    async loadNotificationStep({ commit, rootGetters }) {
      if (rootGetters['userNotification/hasNotifications']) {
        commit('ADD_STEP', {
          key: 'notifications',
          text: 'Actualización IEL',
          doneRequired: false,
        });
      }
      commit('REMOVE_STEP', 'updateData');
      commit('REMOVE_STEP', 'reminders');
      commit('REMOVE_STEP', 'updatePassword');
      commit('REMOVE_STEP', 'shoppingCartAlerts');
      commit('REMOVE_STEP', 'billingModuleStatus');
      commit('REMOVE_STEP', 'notificationPreview');
      commit('REMOVE_STEPS_BY_PREFIX', 'scheduleEventConfirmation');
    },
    loadScheduleEventConfirmationStep({ commit }, pendingEvents) {
      commit('REMOVE_STEPS_BY_PREFIX', 'scheduleEventConfirmation');
      commit('REMOVE_STEP', 'updatePassword');
      commit('REMOVE_STEP', 'billingModuleStatus');
      commit('REMOVE_STEP', 'notifications');
      commit('REMOVE_STEP', 'updateData');
      commit('REMOVE_STEP', 'reminders');
      commit('REMOVE_STEP', 'recentOrders');
      commit('REMOVE_STEP', 'notificationPreview');
      commit('REMOVE_STEP', 'shoppingCartAlerts');

      pendingEvents.forEach((event) => {
        commit('ADD_STEP', {
          key: `scheduleEventConfirmation-${event.id}`,
          text: `Evento de ${moment(event.startDate).format(
            DateFormats.DISPLAY_TIME_AMPM
          )} a ${moment(event.endDate).format(DateFormats.DISPLAY_TIME_AMPM)}`,
          doneRequired: false,
          event: event,
        });
      });
    },
    async loadModalSteps({ commit, dispatch, rootGetters }) {
      const userId = rootGetters['auth/userCode'];

      if (userId) {
        await dispatch('buildSteps');
      } else {
        commit('CLEAN_STEPS');
        dispatch('reminders/cleanReminders', null, { root: true });
        dispatch('recentOrders/cleanRecentOrders', null, { root: true });
      }
    },

    changeCurrentStepValue({ commit }, newStepValue) {
      commit('CHANGE_STEP', newStepValue);
    },

    async buildSteps({ dispatch }) {
      await dispatch('loadSteps');
      await dispatch('syncSteps');
    },

    syncSteps({ state, commit, rootGetters }) {
      if (rootGetters['updatePassword/wantUpdatePassword']) {
        const passwordStatus = rootGetters['auth/passwordStatus'];
        const doneRequired =
          passwordStatus === PASSWORD_STATUS.MUST_CHANGE ||
          passwordStatus === PASSWORD_STATUS.EXPIRED;

        commit('ADD_STEP', {
          key: 'updatePassword',
          text: 'Actualización de contraseña',
          doneRequired,
        });
      } else {
        commit('REMOVE_STEP', 'updatePassword');
      }
      if (rootGetters['moduleMaintenanceStatus/isBillingModuleActive']) {
        commit('ADD_STEP', {
          key: 'billingModuleStatus',
          text: 'Ventana de Mantenimiento',
          doneRequired: false,
        });
      } else {
        commit('REMOVE_STEP', 'billingModuleStatus');
      }
      if (rootGetters['userNotification/hasNotifications']) {
        commit('ADD_STEP', {
          key: 'notifications',
          text: 'Actualización IEL',
          doneRequired: false,
        });
      } else {
        commit('REMOVE_STEP', 'notifications');
      }
      if (rootGetters['updateData/mustUpdateData']) {
        commit('ADD_STEP', {
          key: 'updateData',
          text: 'Actualización de datos de contacto',
          doneRequired: true,
        });
      } else {
        commit('REMOVE_STEP', 'updateData');
      }
      if (rootGetters['reminders/hasAvailableReminders']) {
        commit('ADD_STEP', {
          key: 'reminders',
          text: 'Backorders',
          doneRequired: false,
        });
      } else {
        commit('REMOVE_STEP', 'reminders');
      }
      if (rootGetters['recentOrders/hasUnprocessedRecentOrders']) {
        commit('ADD_STEP', {
          key: 'recentOrders',
          text: 'Carritos',
          doneRequired: false,
        });
      } else {
        commit('REMOVE_STEP', 'recentOrders');
      }

      if (rootGetters['userNotification/selectedNotificationDetails']) {
        commit('ADD_STEP', {
          key: 'notificationPreview',
          text: 'Actualización IEL',
          doneRequired: false,
        });
      } else {
        commit('REMOVE_STEP', 'notificationPreview');
      }

      state.steps
        .filter((s) => s.key.startsWith('scheduleEventConfirmation'))
        .forEach((s) => commit('REMOVE_STEP', s.key));

      if (rootGetters['scheduleNotification/shouldShowConfirmationModal']) {
        const pendingEvents =
          rootGetters['scheduleNotification/pendingConfirmationEvents'];

        pendingEvents.forEach((event) => {
          commit('ADD_STEP', {
            key: `scheduleEventConfirmation-${event.id}`,
            text: `Evento de ${moment(event.startDate).format(
              DateFormats.DISPLAY_TIME_AMPM
            )} a ${moment(event.endDate).format(
              DateFormats.DISPLAY_TIME_AMPM
            )}`,
            doneRequired: false,
            event: event,
          });
        });
      }
    },

    addManualStep({ commit }, step) {
      commit('ADD_STEP', {
        key: step.key,
        text: step.text,
        doneRequired: step.doneRequired || false,
      });
      commit('REMOVE_STEP', 'notifications');
      commit('REMOVE_STEP', 'reminders');
      commit('REMOVE_STEP', 'updateData');
      commit('REMOVE_STEP', 'updatePassword');
      commit('REMOVE_STEP', 'shoppingCartAlerts');
      commit('REMOVE_STEP', 'billingModuleStatus');
      commit('REMOVE_STEP', 'notificationPreview');
      commit('REMOVE_STEPS_BY_PREFIX', 'scheduleEventConfirmation');
    },

    async loadSteps({ dispatch }) {
      await Promise.all([
        dispatch('reminders/loadReminders', null, { root: true }),
        dispatch('recentOrders/loadRecentOrders', null, { root: true }),
        dispatch('userNotification/checkUserNotification', null, {
          root: true,
        }),
        dispatch('updateData/checkSetNewUserInfo', null, { root: true }),
        dispatch('updatePassword/checkMustChangePassword', true, {
          root: true,
        }),
        dispatch('moduleMaintenanceStatus/checkBillingModuleActive', true, {
          root: true,
        }),
        dispatch('scheduleNotification/loadTodayEvents', null, { root: true }),
      ]);
    },

    findStepByKey({ getters }, key) {
      return getters.steps.find((s) => s.key === key) || null;
    },

    removeStep({ commit, state }, key) {
      commit('REMOVE_STEP', key);

      if (state.steps.length > 0) {
        if (state.currentStep > state.steps.length) {
          commit('CHANGE_STEP', state.steps.length);
        }
      } else {
        // if no more steps, close modal
        commit('SET_SHOW_MODAL', false);
      }
    },

    updateStepStatus({ state }, step) {
      const index = state.steps.findIndex((s) => s.key === step.key);
      if (index >= 0) {
        state.steps.splice(index, 1, step);
      }
    },

    updateModalState({ commit }, state) {
      commit('SET_SHOW_MODAL', state);
    },
  },
};
